import { Result, SalesChannel } from '../dto/model';
import {
  LostReasonAssignmentConfigDto,
  LostReasonAssignmentGroupDto,
  LostReasonAssignmentReasonDto,
  LostReasonChannelAdminBundleDto,
  LostReasonChannelConfigurationAdminDto,
  LostReasonChannelGroupAdminDto,
  LostReasonChannelGroupSaveDto,
  LostReasonDto,
  HandoverSystemDto,
  SourceSystemLostReasonDto,
} from '../dto/lost-reason-models';

const rootUrl = (process as any).env.REACT_APP_API_ROOT_URL;

function authHeader(accessToken: string): HeadersInit {
  return { Authorization: `Bearer ${accessToken}` };
}

function jsonHeaders(accessToken: string): HeadersInit {
  return { ...authHeader(accessToken), 'Content-Type': 'application/json' };
}

async function readJsonResult<T>(response: Response): Promise<Result<T>> {
  return (await response.json()) as Result<T>;
}

async function mapDataIfSuccess<TIn, TOut>(
  response: Response,
  mapper: (data: TIn) => TOut,
): Promise<Result<TOut>> {
  const raw = (await response.json()) as Result<TIn>;
  if (raw.isSuccess && raw.data != null) {
    return { ...raw, data: mapper(raw.data) } as Result<TOut>;
  }
  return raw as unknown as Result<TOut>;
}

export async function getSourceSystemLostReasons(
  accessToken: string,
  activeOnly: boolean,
): Promise<Result<SourceSystemLostReasonDto[]>> {
  const response = await fetch(`${rootUrl}/api/v1/source-system-lost-reasons?activeOnly=${activeOnly}`, {
    headers: authHeader(accessToken),
  });
  return readJsonResult<SourceSystemLostReasonDto[]>(response);
}

export async function saveSourceSystemLostReason(
  accessToken: string,
  dto: SourceSystemLostReasonDto,
): Promise<Result<string>> {
  const body = {
    id: dto.id || null,
    lostReasonId: dto.lostReasonId,
    sourceSystem: dto.sourceSystem,
    englishLabel: dto.englishLabel,
    germanLabel: dto.germanLabel,
    isActive: dto.isActive,
  };
  const response = await fetch(`${rootUrl}/api/v1/source-system-lost-reasons`, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify(body),
  });
  const result = await mapDataIfSuccess<unknown, string>(response, (id) => String(id));
  return result;
}

export async function setSourceSystemLostReasonActive(
  accessToken: string,
  id: string,
  isActive: boolean,
): Promise<Result<unknown>> {
  const response = await fetch(`${rootUrl}/api/v1/source-system-lost-reasons/${id}`, {
    method: 'PATCH',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ isActive }),
  });
  return readJsonResult<unknown>(response);
}

export async function permanentDeleteSourceSystemLostReason(
  accessToken: string,
  id: string,
): Promise<Result<unknown>> {
  const response = await fetch(`${rootUrl}/api/v1/source-system-lost-reasons/${id}`, {
    method: 'DELETE',
    headers: authHeader(accessToken),
  });
  const raw = await readJsonResult<unknown>(response);
  return raw;
}

export async function getLostReasons(accessToken: string, activeOnly: boolean): Promise<Result<LostReasonDto[]>> {
  const response = await fetch(`${rootUrl}/api/v1/lost-reasons?activeOnly=${activeOnly}`, {
    headers: authHeader(accessToken),
  });
  return readJsonResult<LostReasonDto[]>(response);
}

export async function getAvailableLostReasonsForChannel(
  accessToken: string,
  salesChannel: SalesChannel,
  excludeGroupId?: string | null,
): Promise<Result<LostReasonDto[]>> {
  const params = excludeGroupId ? `?excludeGroupId=${excludeGroupId}` : '';
  const response = await fetch(`${rootUrl}/api/v1/lost-reasons/available-for-channel/${salesChannel}${params}`, {
    headers: authHeader(accessToken),
  });
  return readJsonResult<LostReasonDto[]>(response);
}

export async function saveLostReason(accessToken: string, dto: LostReasonDto): Promise<Result<string>> {
  const body = {
    id: dto.id || null,
    englishLabel: dto.englishLabel,
    germanLabel: dto.germanLabel,
    isActive: dto.isActive,
  };
  const response = await fetch(`${rootUrl}/api/v1/lost-reasons`, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify(body),
  });
  const result = await mapDataIfSuccess<unknown, string>(response, (id) => String(id));
  return result;
}

export async function setLostReasonActive(
  accessToken: string,
  id: string,
  isActive: boolean,
): Promise<Result<unknown>> {
  const response = await fetch(`${rootUrl}/api/v1/lost-reasons/${id}`, {
    method: 'PATCH',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ isActive }),
  });
  const raw = await readJsonResult<unknown>(response);
  return raw;
}

export async function permanentDeleteLostReason(accessToken: string, id: string): Promise<Result<unknown>> {
  const response = await fetch(`${rootUrl}/api/v1/lost-reasons/${id}`, {
    method: 'DELETE',
    headers: authHeader(accessToken),
  });
  const raw = await readJsonResult<unknown>(response);
  return raw;
}

export async function getLostReasonChannelAdmin(
  accessToken: string,
): Promise<Result<LostReasonChannelAdminBundleDto>> {
  const response = await fetch(`${rootUrl}/api/v1/lost-reasons/group`, { headers: authHeader(accessToken) });
  return readJsonResult<LostReasonChannelAdminBundleDto>(response);
}

export async function saveLostReasonChannelConfiguration(
  accessToken: string,
  dto: LostReasonChannelConfigurationAdminDto,
): Promise<Result<string>> {
  const body = {
    id: dto.id || null,
    salesChannel: dto.salesChannel,
    name: dto.name,
    description: dto.description ?? null,
    isActive: dto.isActive,
  };
  const response = await fetch(`${rootUrl}/api/v1/lost-reasons/configurations`, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify(body),
  });
  const result = await mapDataIfSuccess<unknown, string>(response, (id) => String(id));
  return result;
}

export async function setLostReasonChannelConfigurationActive(
  accessToken: string,
  id: string,
  isActive: boolean,
): Promise<Result<unknown>> {
  const response = await fetch(`${rootUrl}/api/v1/lost-reasons/configurations/${id}`, {
    method: 'PATCH',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ isActive }),
  });
  const raw = await readJsonResult<unknown>(response);
  return raw;
}

export async function permanentDeleteLostReasonChannelConfiguration(
  accessToken: string,
  id: string,
): Promise<Result<unknown>> {
  const response = await fetch(`${rootUrl}/api/v1/lost-reasons/configurations/${id}`, {
    method: 'DELETE',
    headers: authHeader(accessToken),
  });
  const raw = await readJsonResult<unknown>(response);
  return raw;
}

export async function saveLostReasonChannelGroup(
  accessToken: string,
  dto: LostReasonChannelGroupSaveDto,
): Promise<Result<string>> {
  const body = {
    id: dto.id || null,
    configurationId: dto.configurationId,
    groupName: dto.groupName,
    outcomeKey: dto.outcomeKey,
    handoverSystemKeyword: dto.handoverSystemKeyword ?? null,
    isActive: dto.isActive,
    lostReasonIds: dto.lostReasonIds,
  };
  const response = await fetch(`${rootUrl}/api/v1/lost-reasons/group`, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify(body),
  });
  const result = await mapDataIfSuccess<unknown, string>(response, (id) => String(id));
  return result;
}

export async function setLostReasonChannelGroupActive(
  accessToken: string,
  id: string,
  isActive: boolean,
): Promise<Result<unknown>> {
  const response = await fetch(`${rootUrl}/api/v1/lost-reasons/group/${id}`, {
    method: 'PATCH',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ isActive }),
  });
  const raw = await readJsonResult<unknown>(response);
  return raw;
}

export async function permanentDeleteLostReasonChannelGroup(
  accessToken: string,
  id: string,
): Promise<Result<unknown>> {
  const response = await fetch(`${rootUrl}/api/v1/lost-reasons/group/${id}`, {
    method: 'DELETE',
    headers: authHeader(accessToken),
  });
  const raw = await readJsonResult<unknown>(response);
  return raw;
}

export async function getHandoverSystems(
  accessToken: string,
  activeOnly: boolean,
): Promise<Result<HandoverSystemDto[]>> {
  const response = await fetch(`${rootUrl}/api/v1/handover-systems?activeOnly=${activeOnly}`, {
    headers: authHeader(accessToken),
  });
  return readJsonResult<HandoverSystemDto[]>(response);
}

export async function saveHandoverSystem(
  accessToken: string,
  dto: HandoverSystemDto,
): Promise<Result<string>> {
  const body = {
    id: dto.id || null,
    keyword: dto.keyword,
    description: dto.description,
    isActive: dto.isActive,
  };
  const response = await fetch(`${rootUrl}/api/v1/handover-systems`, {
    method: 'POST',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify(body),
  });
  const result = await mapDataIfSuccess<unknown, string>(response, (id) => String(id));
  return result;
}

export async function setHandoverSystemActive(
  accessToken: string,
  id: string,
  isActive: boolean,
): Promise<Result<unknown>> {
  const response = await fetch(`${rootUrl}/api/v1/handover-systems/${id}`, {
    method: 'PATCH',
    headers: jsonHeaders(accessToken),
    body: JSON.stringify({ isActive }),
  });
  const raw = await readJsonResult<unknown>(response);
  return raw;
}

export async function permanentDeleteHandoverSystem(
  accessToken: string,
  id: string,
): Promise<Result<unknown>> {
  const response = await fetch(`${rootUrl}/api/v1/handover-systems/${id}`, {
    method: 'DELETE',
    headers: authHeader(accessToken),
  });
  const raw = await readJsonResult<unknown>(response);
  return raw;
}

export async function getLostReasonAssignmentConfig(
  accessToken: string,
  salesChannel: SalesChannel,
): Promise<Result<LostReasonAssignmentConfigDto>> {
  const response = await fetch(
    `${rootUrl}/api/v1/lost-reasons/group?salesChannel=${encodeURIComponent(salesChannel)}`,
    { headers: authHeader(accessToken) },
  );
  return readJsonResult<LostReasonAssignmentConfigDto>(response);
}

export type { LostReasonChannelGroupAdminDto, LostReasonAssignmentGroupDto, LostReasonAssignmentReasonDto };
